import { useEffect, useRef, useState } from "react";
import flvjs from "flv.js";
import socket from "services/socket";
import { Maximize, Pause, Play, Volume2, VolumeOffIcon } from "lucide-react";
import { AppLog } from "services";
import { StatusMsg } from "utils";

export default function LivePostContentFlv({ post }: { post: any }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const flvPlayerRef = useRef<flvjs.Player | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [viewerCount, setViewerCount] = useState(0);
  const [streamEnded, setStreamEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isLive, setIsLive] = useState(true);
  const [volume, setVolume] = useState(100);

  const [showVolumeInput, setShowVolumeInput] = useState(false);

  const createPlayer = () => {
    if (!videoRef.current || !flvjs.isSupported()) return;

    const streamUrl = post.media_url;

    const player = flvjs.createPlayer({
      type: "flv",
      url: streamUrl,
      isLive: true,
    });

    player.attachMediaElement(videoRef.current);
    player.load();
    player.play();

    flvPlayerRef.current = player;

    // Live badge polling using seekable.end(0)
    intervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (video && video.seekable.length > 0) {
        const liveEdge = video.seekable.end(0);
        const current = video.currentTime;
        setIsLive(liveEdge - current < 5);
      }
    }, 1000);
  };

  const destroyPlayer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (flvPlayerRef.current) {
      flvPlayerRef.current.destroy();
      flvPlayerRef.current = null;
    }
  };

  const handleGoLive = () => {
    destroyPlayer();
    setTimeout(() => {
      createPlayer();
    }, 200);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const onSeekVolume = (volume: number) => {
    try {
      setVolume(volume);
      if (videoRef.current) {
        videoRef.current.volume = volume / 100;
        videoRef.current.muted = volume === 0;
        setIsMuted(videoRef.current.muted);
      }
    } catch (error) {
      AppLog.show({
        message: `[onSeekVolume]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  useEffect(() => {
    const streamingKey = post?.media_key;
    if (!streamingKey) return;

    socket.emit("viewerJoin", streamingKey);
    socket.on("viewerCountUpdate", (count) => setViewerCount(count));
    socket.on("OnFlipFlopStreamEnded", () => {
      setStreamEnded(true);
      destroyPlayer();
    });

    return () => {
      socket.emit("viewerLeave", streamingKey);
      socket.off("viewerCountUpdate");
      socket.off("OnFlipFlopStreamEnded");
    };
  }, [post]);

  useEffect(() => {
    if (!post) return;
    createPlayer();

    return () => {
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  return (
    <div className="flex w-full h-full lg:h-screen bg-black text-white relative">
      <div className="relative w-full h-full">
        <video ref={videoRef} muted autoPlay playsInline className="w-full h-full rounded bg-black" controls={false} />

        {/* Top Right Badges */}
        <div className="absolute top-3 right-3 flex space-x-2 z-10">
          {/* LIVE Badge */}
          <div className={`text-white text-xs font-bold px-3 py-1 rounded ${isLive ? "bg-red-600" : "bg-gray-500"}`} onClick={handleGoLive}>
            LIVE
          </div>

          {/* Viewer Count */}
          <div className="bg-black bg-opacity-70 text-white text-xs px-3 py-1 rounded flex items-center space-x-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>{viewerCount}</span>
          </div>
        </div>

        {/* Custom Controls */}
        <div className="absolute bottom-4 w-full px-4 flex items-center justify-between text-white z-50 ">
          {/* Left controls: Play + Volume + Slider */}
          <div className="flex items-center space-x-2">
            {isPlaying ? (
              <Pause onClick={togglePlay} className="cursor-pointer rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
            ) : (
              <Play onClick={togglePlay} className="cursor-pointer rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
            )}

            <div
              className="flex items-center space-x-2"
              onClick={() => {}}
              onMouseMove={() => {
                setShowVolumeInput(true);
              }}
              onMouseLeave={() => setShowVolumeInput(false)}
            >
              <button className="text-white" onClick={toggleMute}>
                {isMuted ? (
                  <VolumeOffIcon className="rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                ) : (
                  <Volume2 className="rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
                )}
              </button>

              {showVolumeInput && (
                <div className=" bg-black size-5 rounded-2xl bg-opacity-10 text-white max-sm:w-24 w-28 p-2 items-center flex pt-4 pb-4">
                  <input type="range" min={0} max={100} value={volume} onChange={(e) => onSeekVolume(parseFloat(e.target.value))} className="max-sm:w-20 w-24 " />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button onClick={handleGoLive} className="flex items-center space-x-2 text-white font-semibold">
              <span className={`w-2 h-2 rounded-full ${isLive ? "bg-red-600" : "bg-gray-400"}`} />
              <span>Live</span>
            </button>
            <Maximize onClick={toggleFullscreen} className="cursor-pointer rounded-full bg-opacity-10 bg-black p-1 max-sm:size-6 size-8" />
          </div>
        </div>

        {streamEnded && <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center text-white text-2xl z-50">🔴 This live stream has ended.</div>}
      </div>
    </div>
  );
}

